<script setup>
import { useI18n } from 'vue-i18n';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';

defineProps({
  disabled: {
    type: Boolean,
    default: false,
  },
});

const form = defineModel({ type: Object, required: true });

const { t } = useI18n();

const fieldClass =
  'w-full h-10 px-3 text-sm rounded-lg bg-n-alpha-black2 text-n-slate-12 outline outline-1 outline-n-weak';
</script>

<template>
  <div class="flex flex-col gap-8">
    <section class="flex flex-col gap-3">
      <h2 class="text-base font-medium text-n-slate-12">
        {{ t('ONIX.ACTIONS.STEP_ABOUT') }}
      </h2>
      <Input
        v-model="form.title"
        :label="t('ONIX.ACTIONS.NAME')"
        :placeholder="t('ONIX.ACTIONS.NAME_PLACEHOLDER')"
        :disabled="disabled"
      />
      <TextArea
        v-model="form.description"
        :label="t('ONIX.ACTIONS.PURPOSE')"
        :placeholder="t('ONIX.ACTIONS.PURPOSE_PLACEHOLDER')"
        :max-length="2000"
        :disabled="disabled"
        auto-height
      />
      <label class="flex items-center gap-2 text-sm text-n-slate-12">
        <input v-model="form.enabled" type="checkbox" :disabled="disabled" />
        {{ t('ONIX.ACTIONS.ENABLED') }}
      </label>
    </section>

    <section class="flex flex-col gap-3">
      <h2 class="text-base font-medium text-n-slate-12">
        {{ t('ONIX.ACTIONS.STEP_CONNECT') }}
      </h2>
      <Input
        v-model="form.endpoint_url"
        :label="t('ONIX.ACTIONS.ADDRESS')"
        :placeholder="t('ONIX.ACTIONS.ADDRESS_PLACEHOLDER')"
        :disabled="disabled"
      />
      <label class="flex flex-col gap-1 text-sm text-n-slate-12">
        {{ t('ONIX.ACTIONS.KIND') }}
        <select
          v-model="form.http_method"
          :class="fieldClass"
          :disabled="disabled"
        >
          <option value="GET">{{ t('ONIX.ACTIONS.KIND_LOOKUP') }}</option>
          <option value="POST">{{ t('ONIX.ACTIONS.KIND_SEND') }}</option>
        </select>
      </label>
    </section>

    <section class="flex flex-col gap-3">
      <h2 class="text-base font-medium text-n-slate-12">
        {{ t('ONIX.ACTIONS.STEP_ACCESS') }}
      </h2>
      <label class="flex flex-col gap-1 text-sm text-n-slate-12">
        {{ t('ONIX.ACTIONS.ACCESS') }}
        <select
          v-model="form.auth_type"
          :class="fieldClass"
          :disabled="disabled"
        >
          <option value="none">{{ t('ONIX.ACTIONS.ACCESS_NONE') }}</option>
          <option value="bearer">{{ t('ONIX.ACTIONS.ACCESS_TOKEN') }}</option>
          <option value="basic">{{ t('ONIX.ACTIONS.ACCESS_BASIC') }}</option>
          <option value="api_key">{{ t('ONIX.ACTIONS.ACCESS_SECRET') }}</option>
        </select>
      </label>
      <Input
        v-if="form.auth_type === 'bearer'"
        v-model="form.token"
        type="password"
        :label="t('ONIX.ACTIONS.TOKEN')"
        :disabled="disabled"
      />
      <div
        v-else-if="form.auth_type === 'basic'"
        class="grid grid-cols-1 sm:grid-cols-2 gap-3"
      >
        <Input
          v-model="form.username"
          :label="t('ONIX.ACTIONS.USERNAME')"
          :disabled="disabled"
        />
        <Input
          v-model="form.password"
          type="password"
          :label="t('ONIX.ACTIONS.PASSWORD')"
          :disabled="disabled"
        />
      </div>
      <Input
        v-else-if="form.auth_type === 'api_key'"
        v-model="form.apiKey"
        type="password"
        :label="t('ONIX.ACTIONS.SECRET')"
        :disabled="disabled"
      />
    </section>

    <section class="flex flex-col gap-3">
      <h2 class="text-base font-medium text-n-slate-12">
        {{ t('ONIX.ACTIONS.STEP_REPLY') }}
      </h2>
      <TextArea
        v-model="form.response_template"
        :label="t('ONIX.ACTIONS.REPLY')"
        :placeholder="t('ONIX.ACTIONS.REPLY_HINT')"
        :max-length="4000"
        :disabled="disabled"
        auto-height
      />
      <TextArea
        v-model="form.request_template"
        :label="t('ONIX.ACTIONS.SEND_DATA')"
        :placeholder="t('ONIX.ACTIONS.SEND_DATA_HINT')"
        :max-length="8000"
        :disabled="disabled"
        auto-height
      />
    </section>
  </div>
</template>
